// Chargement paresseux et fiable des bibliothèques PDF (jsPDF + jspdf-autotable).
//
// Pourquoi ce module : selon la façon dont un paquet est empaqueté (ESM, CJS ou
// UMD) et dont le bundler interopère ses exports, la fonction recherchée peut
// être exposée comme export nommé OU uniquement via `default` (et parfois
// `default.default`). En production, un import nommé direct peut donc renvoyer
// `undefined` et provoquer l'erreur « x is not a function » au moment de générer
// le PDF. Ici on résout explicitement le bon export, une seule fois.

import type { jsPDF } from "jspdf";

type JsPDFConstructor = typeof jsPDF;
type AutoTableFn = (doc: jsPDF, options: Record<string, unknown>) => void;

export interface PdfLibs {
  JsPDF: JsPDFConstructor;
  autoTable: AutoTableFn;
}

type UnknownRecord = Record<string, unknown>;

/**
 * Résout une fonction depuis un module importé dynamiquement, en couvrant tous
 * les cas d'interop possible :
 *   - export nommé à la racine du namespace ;
 *   - export par défaut (fonction) ;
 *   - export par défaut sur `default[clé]` ou `default.default` (interop CJS).
 */
function resolveExportedFn(source: unknown, key: string): unknown {
  if (typeof source === "function") return source;
  if (source && typeof source === "object") {
    const record = source as UnknownRecord;
    if (typeof record[key] === "function") return record[key];
    if (typeof record.default === "function") return record.default;
    if (record.default && typeof record.default === "object") {
      const fallback = record.default as UnknownRecord;
      if (typeof fallback[key] === "function") return fallback[key];
      if (typeof fallback.default === "function") return fallback.default;
    }
  }
  return undefined;
}

let cachedPromise: Promise<PdfLibs> | null = null;

async function resolvePdfLibs(): Promise<PdfLibs> {
  const [jspdfModule, autoTableModule] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  const JsPDF = resolveExportedFn(jspdfModule, "jsPDF") as JsPDFConstructor | undefined;
  const autoTable = resolveExportedFn(autoTableModule, "autoTable") as AutoTableFn | undefined;

  if (typeof JsPDF !== "function") {
    throw new Error("La bibliothèque PDF (jsPDF) n'a pas pu être chargée.");
  }
  if (typeof autoTable !== "function") {
    throw new Error("La bibliothèque de tableaux PDF (jspdf-autotable) n'a pas pu être chargée.");
  }

  return { JsPDF, autoTable };
}

/** Charge jsPDF + jspdf-autotable à la demande, une seule fois par session. */
export function loadPdfLibs(): Promise<PdfLibs> {
  if (!cachedPromise) {
    cachedPromise = resolvePdfLibs().catch((error: unknown) => {
      // Ne pas mémoriser un échec : la prochaine tentative doit pouvoir réessayer.
      cachedPromise = null;
      throw error;
    });
  }
  return cachedPromise;
}